import { useCallback, useMemo, useState } from "react"
import { useRefreshPassport } from "@mezo-org/passport"
import { PayloadAction } from "@reduxjs/toolkit"

import {
  increaseDebt,
  increaseDebtAndCollateral,
  selectCollateralPrice,
  selectUpdateLoanAmount,
  selectUpdateStrategy,
  selectUserLoan,
  selectWalletAddress,
  setUpdateLoanAmount,
  useDappDispatch,
  useDappSelector,
} from "#/redux-state"
import { BorrowMoreStrategy } from "#/shared/types"
import {
  bigIntToFloat,
  bigIntToHumanReadableFormat,
  floatToBigInt,
  normalizeDecimalNumberInput,
  separateThousandsWithComma,
  trimDecimals,
} from "#/shared/utils"

import { usePosthogBorrowMoreEvent } from "../posthog-events"

import {
  useCalculateCollateralAmountBasedOnRatio,
  useCollateralizationRatioForExistingLoan,
} from "./borrow-calculation-helpers"
import { useBorrowingFee, useFetchAllBorrowData } from "./borrow-system"

export const useUpdateBorrowedAmount = () => {
  const dispatch = useDappDispatch()
  const updateLoanAmount = useDappSelector(selectUpdateLoanAmount)

  const onAdditionalAmountChange = useCallback(
    (additionalAmount: string) => {
      const normalizedLoanAmount = normalizeDecimalNumberInput(additionalAmount)
      const trimmedLoan = trimDecimals(normalizedLoanAmount, 2)

      dispatch(setUpdateLoanAmount(trimmedLoan))
    },
    [dispatch],
  )

  const isAdditionalAmountValid = useMemo(() => {
    const amount = floatToBigInt(updateLoanAmount)

    if (!amount || amount <= 0) return false

    return true
  }, [updateLoanAmount])

  return {
    additionalAmount: updateLoanAmount,
    onAdditionalAmountChange,
    isAdditionalAmountValid,
  }
}

// Calculate collateral amount needed to keep the existing loan ratio unchanged
export const useUpdatedCollateralBasedOnRatio = (newLoanAmount: bigint) => {
  const currentLoan = useDappSelector(selectUserLoan)
  const collateralPrice = useDappSelector(selectCollateralPrice)

  const { calculateBorrowingFee } = useBorrowingFee()

  // We want to use the old collateralization ratio to calculate the new collateral amount
  // required to keep the ratio unchanged
  const { collateralizationRatio } = useCollateralizationRatioForExistingLoan(
    currentLoan?.debtAmount ?? 0n,
    0n,
    currentLoan?.collateralAmount ?? 0n,
    0n,
  )

  const { calculateCollateralAmountBasedOnRatio } =
    useCalculateCollateralAmountBasedOnRatio()

  const newLoanWithFeesValue = useMemo(() => {
    const debt = currentLoan?.debtAmount ?? 0n

    if (!newLoanAmount) return debt

    if (newLoanAmount < 0n) return debt + newLoanAmount

    return debt + newLoanAmount + calculateBorrowingFee(newLoanAmount)
  }, [currentLoan, newLoanAmount, calculateBorrowingFee])

  const updatedCollateral = useMemo(() => {
    if (!collateralizationRatio || !newLoanWithFeesValue || !collateralPrice)
      return 0n

    // If there is no change to the loan then we can return the current collateral amount
    // and avoid rounding errors that hapeen during the division
    if (!newLoanAmount) return currentLoan?.collateralAmount ?? 0n

    // Adjusted formula to correctly calculate the required collateral

    const newCollateral = calculateCollateralAmountBasedOnRatio(
      newLoanWithFeesValue,
      collateralizationRatio,
    )

    return newCollateral
  }, [
    calculateCollateralAmountBasedOnRatio,
    collateralPrice,
    collateralizationRatio,
    currentLoan?.collateralAmount,
    newLoanAmount,
    newLoanWithFeesValue,
  ])

  const updatedCollateralAmountLabel = useMemo(
    () => bigIntToHumanReadableFormat(updatedCollateral, 18, 8),
    [updatedCollateral],
  )

  const additionalCollateralAmount = useMemo(
    () => updatedCollateral - (currentLoan?.collateralAmount ?? 0n),
    [currentLoan?.collateralAmount, updatedCollateral],
  )

  const additionalCollateralAmountLabel = useMemo(
    () => bigIntToHumanReadableFormat(additionalCollateralAmount, 18, 8),
    [additionalCollateralAmount],
  )

  const previousCollateralAmountLabel = useMemo(
    () =>
      bigIntToHumanReadableFormat(currentLoan?.collateralAmount ?? 0n, 18, 8),
    [currentLoan?.collateralAmount],
  )

  return {
    previousCollateralAmount: currentLoan?.collateralAmount ?? 0n,
    previousCollateralAmountLabel,
    additionalCollateralAmount,
    additionalCollateralAmountLabel,
    updatedCollateral,
    updatedCollateralAmountLabel,
  }
}

export const useUpdateBorrowedAmountTransaction = () => {
  const dispatch = useDappDispatch()
  const updateLoanAmount = useDappSelector(selectUpdateLoanAmount)
  const strategy = useDappSelector(selectUpdateStrategy)

  const {
    emitBorrowMoreIncreaseCollErrorEvent,
    emitBorrowMoreIncreaseCollEvent,
    emitBorrowMoreReduceRatioErrorEvent,
    emitBorrowMoreReduceRatioEvent,
  } = usePosthogBorrowMoreEvent()

  const { additionalCollateralAmount } = useUpdatedCollateralBasedOnRatio(
    floatToBigInt(updateLoanAmount),
  )

  const { fetchAllBorrowData } = useFetchAllBorrowData()
  const { refreshTokensBalances } = useRefreshPassport()

  const address = useDappSelector(selectWalletAddress)

  const [hasError, setHasError] = useState(false)
  const [isLoading, setIsLoading] = useState(false)

  const borrowMoreHandler = useCallback(async () => {
    setIsLoading(true)
    setHasError(false)

    const normalizedLoanAmount = floatToBigInt(updateLoanAmount)
    let success = false

    const debtChangeAmount = bigIntToFloat(normalizedLoanAmount ?? 0n, 18, 4)

    const collateralChangeAmount = bigIntToFloat(
      additionalCollateralAmount ?? 0n,
      18,
      4,
    )

    if (strategy === BorrowMoreStrategy.DECREASE_RATIO) {
      const result = (await dispatch(
        increaseDebt(normalizedLoanAmount),
      )) as PayloadAction<boolean>

      success = result.payload

      if (success) {
        emitBorrowMoreReduceRatioEvent(address!, debtChangeAmount)
      } else {
        emitBorrowMoreReduceRatioErrorEvent(address!, debtChangeAmount)
      }
    } else {
      const result = (await dispatch(
        increaseDebtAndCollateral({
          debtChange: normalizedLoanAmount,
          collateralAmount: additionalCollateralAmount,
        }),
      )) as PayloadAction<boolean>

      success = result.payload

      if (success) {
        emitBorrowMoreIncreaseCollEvent(
          address!,
          debtChangeAmount,
          collateralChangeAmount,
        )
      } else {
        emitBorrowMoreIncreaseCollErrorEvent(
          address!,
          debtChangeAmount,
          collateralChangeAmount,
        )
      }
    }

    if (success) {
      await fetchAllBorrowData({ refetch: true })
      await refreshTokensBalances()
    }

    setIsLoading(false)
    setHasError(!success)
  }, [
    updateLoanAmount,
    additionalCollateralAmount,
    strategy,
    dispatch,
    emitBorrowMoreReduceRatioEvent,
    address,
    emitBorrowMoreReduceRatioErrorEvent,
    emitBorrowMoreIncreaseCollEvent,
    emitBorrowMoreIncreaseCollErrorEvent,
    fetchAllBorrowData,
    refreshTokensBalances,
  ])

  return {
    borrowMoreHandler,
    hasError,
    isLoading,
  }
}

export const useUpdateCollateralizationRatio = () => {
  const userLoan = useDappSelector(selectUserLoan)

  const updateLoanAmount = useDappSelector(selectUpdateLoanAmount)

  // We need to calculate old collateralization ratio based on the current loan amount
  const {
    loanWithFeesValue: prevLoanWithFeesValue,
    collateralizationRatioLabel: prevCollateralizationRatioLabel,
    collateralizationRatio: prevCollateralizationRatio,
  } = useCollateralizationRatioForExistingLoan(
    userLoan?.debtAmount ?? 0n,
    0n,
    userLoan?.collateralAmount ?? 0n,
    0n,
  )

  const {
    loanWithFeesValue: newLoanWithFeesValue,
    collateralizationRatioLabel: newCollateralizationRatioLabel,
    collateralizationRatio: newCollateralizationRatio,
  } = useCollateralizationRatioForExistingLoan(
    userLoan?.debtAmount ?? 0n,
    floatToBigInt(updateLoanAmount),
    userLoan?.collateralAmount ?? 0n,
    0n,
  )

  const collateralizationRatioChange = useMemo(
    () =>
      newCollateralizationRatio && prevCollateralizationRatio
        ? newCollateralizationRatio - prevCollateralizationRatio
        : 0n,
    [newCollateralizationRatio, prevCollateralizationRatio],
  )

  const collateralizationRatioChangeLabel = useMemo(
    () =>
      separateThousandsWithComma(
        bigIntToHumanReadableFormat(collateralizationRatioChange * 100n),
        0,
      ),
    [collateralizationRatioChange],
  )

  return {
    prevLoanWithFeesValue,
    newLoanWithFeesValue,
    collateralizationRatioChange,
    collateralizationRatioChangeLabel,
    prevCollateralizationRatio,
    prevCollateralizationRatioLabel,
    newCollateralizationRatio,
    newCollateralizationRatioLabel,
  }
}
